import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { costDateRange, isValidCostPeriod, readCostPeriod, type CostPeriodSelection, type CostPeriod } from '../../utils/aiCostPeriod';
import { useAdminText } from './useAdminText';

export function AICostPeriodFilter() {
  const [params, setParams] = useSearchParams();
  const current = readCostPeriod(params);
  const [draft, setDraft] = useState(current);
  const [invalid, setInvalid] = useState(!isValidCostPeriod(current));
  const t = useAdminText();
  const apply = (value: CostPeriodSelection) => {
    try { costDateRange(value); } catch { setInvalid(true); return; }
    setInvalid(false);
    const next = new URLSearchParams(params);
    next.set('period', value.period);
    for (const field of ['start', 'end'] as const) {
      if (value.period === 'custom') next.set(field, value[field]); else next.delete(field);
    }
    setParams(next);
  };
  return <Stack spacing={1} sx={{ mb: 2 }}>
    <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
      <TextField select size="small" label={t('costPeriod', 'Period')} sx={{ minWidth: 190 }} value={draft.period}
        onChange={event => {
          const value = { ...draft, period: event.target.value as CostPeriod };
          setDraft(value); setInvalid(false);
          if (value.period !== 'custom') apply(value);
        }}>
        {([['day', 'Last 24 hours'], ['week', 'Last 7 days'], ['month', 'Current month'],
          ['threeMonths', 'Last 3 months'], ['custom', 'Custom range'], ['all', 'All time']] as const)
          .map(([value, label]) => <MenuItem key={value} value={value}>{t(`costPeriods.${value}`, label)}</MenuItem>)}
      </TextField>
      {draft.period === 'custom' && <>
        <TextField type="date" size="small" label={t('startDate', 'Start date')} value={draft.start}
          slotProps={{ inputLabel: { shrink: true } }} onChange={event => setDraft({ ...draft, start: event.target.value })} />
        <TextField type="date" size="small" label={t('endDate', 'End date')} value={draft.end}
          slotProps={{ inputLabel: { shrink: true } }} onChange={event => setDraft({ ...draft, end: event.target.value })} />
        <Button variant="outlined" onClick={() => apply(draft)}>{t('applyPeriod', 'Apply period')}</Button>
      </>}
    </Stack>
    {invalid && <Alert severity="error">{t('invalidCostDates', 'Choose valid dates with the end on or after the start.')}</Alert>}
    <Typography variant="caption" color="text.secondary">
      {t('costTimezone', 'Calendar dates use your timezone: %{zone}.', { zone: Intl.DateTimeFormat().resolvedOptions().timeZone })}
      {draft.period === 'custom' && current.period !== 'custom' && ` ${t('costPeriodNotApplied', 'Apply dates to change the displayed period.')}`}
    </Typography>
  </Stack>;
}
